/**
 * Pure helpers for ranking faces by detection frequency and locating
 * the first detection of a given face — used by the Face Mapping admin UI.
 */

import type { FaceDetection, FaceFrequency, FaceTracks } from './types';

/**
 * Returns face labels sorted by detection count, descending.
 * Ties break alphabetically by face label for deterministic ordering.
 */
export function computeFaceFrequencies(
  faceTracks: FaceTracks,
): FaceFrequency[] {
  return Object.entries(faceTracks)
    .map(([faceLabel, ids]) => ({ faceLabel, count: ids.length }))
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.faceLabel.localeCompare(b.faceLabel);
    });
}

/**
 * First chronological detection (lowest `time`) for the given face label,
 * or null if no detection carries that label.
 */
export function getFirstDetectionForFace(
  detections: FaceDetection[],
  faceLabel: string,
): FaceDetection | null {
  let earliest: FaceDetection | null = null;
  for (const d of detections) {
    if (d.label !== faceLabel) continue;
    if (earliest === null || d.time < earliest.time) {
      earliest = d;
    }
  }
  return earliest;
}
